import { CollectionPointSearchParams, Coordinates } from '../types/collectionPoints';
import { joinStreetHouseNumber, joinZipCodeCity } from './address';
import { Address, SimpleAddress } from './checkoutState';
import { Country, config } from './config';
import { reverseGeocode } from './geocode';
import isMultiCountry from './isMultiCountry';
import { pushErrorTrackingEventFromErrorObject } from './tracking';
import { useScriptWithLoadedState } from './useScript';

const getCoordinates = (
  result: google.maps.places.PlaceResult | google.maps.GeocoderResult
): Coordinates | undefined => {
  const location = result.geometry?.location;
  if (!location) {
    return undefined;
  } else {
    return { latitude: location.lat(), longitude: location.lng() };
  }
};

export const getAddress = (
  result: google.maps.places.PlaceResult | google.maps.GeocoderResult,
  isStateSelect?: boolean
): Partial<SimpleAddress & { coordinates: Coordinates }> => {
  const getAddressComponent = (type: string, useShortName?: boolean) =>
    result.address_components!.find(component => component.types.includes(type))?.[
      useShortName ? 'short_name' : 'long_name'
    ];

  const street = getAddressComponent('route', true);
  const streetNumber = getAddressComponent('street_number');
  const premise = getAddressComponent('premise');
  const houseNumber = premise ? `${premise}/${streetNumber}` : streetNumber;
  const zipCode = getAddressComponent('postal_code');
  const city =
    getAddressComponent('locality') ??
    getAddressComponent('postal_town') ??
    getAddressComponent('administrative_area_level_3') ??
    getAddressComponent('sublocality');
  const state = getAddressComponent('administrative_area_level_1', !!isStateSelect);
  const coordinates = getCoordinates(result);

  return { street, houseNumber, zipCode, city, coordinates, state };
};

const getFormattedAddress = (address: SimpleAddress) =>
  `${joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street: address.street,
    houseNumber: address.houseNumber
  })} • ${joinZipCodeCity({
    country: { iso2Code: config.country },
    zipCode: address.zipCode,
    city: address.city
  })}`;

const getMaximumDistance = () => Math.max(...config.collectionPoints.maximumDistanceOptions) * 1000;

export const getInitialSearchParams = (): CollectionPointSearchParams => ({
  streetHouseNumber: '',
  zipCode: '',
  city: '',
  maximumDistance: config.collectionPoints.defaultMaximumDistance * 1000,
  formattedAddress: '',
  type: ''
});

export const getSearchParamsFromAddress = (address: Address): CollectionPointSearchParams => ({
  streetHouseNumber: joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street: address.street,
    houseNumber: address.houseNumber
  }),
  zipCode: address.zipCode ?? '',
  city: address.city,
  maximumDistance: getMaximumDistance(),
  formattedAddress: getFormattedAddress(address),
  type: ''
});

export const getSearchParamsFromCurrentPosition = async (): Promise<CollectionPointSearchParams | undefined> => {
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject)
    );
    const geocodedAddress = await reverseGeocode(position.coords);
    const { street, houseNumber, zipCode, city } = geocodedAddress;
    if (!street || !houseNumber || !zipCode || !city) {
      return undefined;
    }
    return {
      streetHouseNumber: joinStreetHouseNumber({ country: { iso2Code: config.country }, street, houseNumber }),
      zipCode,
      city,
      maximumDistance: getMaximumDistance(),
      coordinates: position.coords,
      formattedAddress: getFormattedAddress(geocodedAddress as SimpleAddress)
    };
  } catch (e) {
    console.error(e);
    pushErrorTrackingEventFromErrorObject(e, 'collection_points_current_location');
    return undefined;
  }
};

export const getSearchParamsFromPlace = (
  place: google.maps.places.PlaceResult
): CollectionPointSearchParams | undefined => {
  if (!place.address_components) {
    return undefined;
  }
  const address = getAddress(place);
  const { street, city, coordinates, state } = address;
  if (!street || !city) {
    return undefined;
  }
  const houseNumber = address.houseNumber ?? '1';
  const streetHouseNumber = joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street,
    houseNumber
  });
  const zipCode = address.zipCode ?? '0000';
  return {
    streetHouseNumber,
    zipCode,
    city,
    maximumDistance: getMaximumDistance(),
    coordinates,
    formattedAddress: getFormattedAddress(address as SimpleAddress),
    state
  };
};

export const getAutocompleteAddressFromPlace = (
  place: google.maps.places.PlaceResult,
  countryFromPlace: Country,
  isStateSelect?: boolean
): (SimpleAddress & { streetHouseNumber: string }) | undefined => {
  if (!place.address_components) {
    return undefined;
  }
  const address = getAddress(place, !!isStateSelect);
  const { street, city, state } = address;
  if (!street || !city) {
    return undefined;
  }
  const houseNumber = address.houseNumber ?? '';
  const streetHouseNumber = joinStreetHouseNumber({
    country: { iso2Code: isMultiCountry(config.country) ? countryFromPlace : config.country },
    street,
    houseNumber
  });
  const zipCode = address.zipCode ?? '';
  return {
    streetHouseNumber,
    houseNumber,
    street,
    state,
    zipCode,
    city
  };
};

const googleMapsApiKeys: Record<string, Record<string, string>> = {
  aboutYou: { staging: GOOGLE_MAPS_API_KEY_ABOUT_YOU_STAGING, production: GOOGLE_MAPS_API_KEY_ABOUT_YOU_PRODUCTION },
  aboutYouOutlet: {
    staging: GOOGLE_MAPS_API_KEY_ABOUT_YOU_OUTLET_STAGING,
    production: GOOGLE_MAPS_API_KEY_ABOUT_YOU_OUTLET_PRODUCTION
  },
  scayle: { staging: GOOGLE_MAPS_API_KEY_SCAYLE_STAGING, production: GOOGLE_MAPS_API_KEY_SCAYLE_PRODUCTION }
};

export const useGoogleMapsSdk = (isEnabled?: boolean) => {
  const LIBRARIES = { PLACES: 'places', MARKER: 'marker' };
  const GOOGLE_MAPS_BASE_URL = 'https://maps.googleapis.com/maps/api/js';

  const shopsMapping: Record<string, string> = { ay: 'aboutYou', ao: 'aboutYouOutlet' };
  const shop = shopsMapping[config.shop] ?? 'scayle';
  const environment = config.isProduction ? 'production' : 'staging';
  const librariesToLoad = Object.values(LIBRARIES).join(',');

  const googleMapsOptions = {
    key: googleMapsApiKeys[shop][environment],
    ...(isMultiCountry(config.country) ?
      { language: config.locale.substring(0, 2) }
    : { region: config.country, language: config.locale.replace('_', '-') })
  };

  return useScriptWithLoadedState({
    url: `${GOOGLE_MAPS_BASE_URL}?${new URLSearchParams(googleMapsOptions)}&libraries=${librariesToLoad}`,
    isEnabled,
    initializationCheck: () => typeof window.google !== 'undefined'
  });
};
